import { useMutation, useQueryClient } from '@tanstack/react-query';
import { message } from 'antd';

import { i18n } from '../../../i18n/index.js';
import { getEntityActivitiesQueryKey } from '../../activity/api/activities.client.js';
import { getCompanyDetailQueryKey } from '../../companies/api/companies.client.js';
import { getContactDetailQueryKey } from '../../contacts/api/contacts.client.js';
import { getDealDetailQueryKey } from '../../deals/api/deals.client.js';
import { getDocumentDetailQueryKey } from '../../documents/api/documents.client.js';
import { getTaskDetailQueryKey } from '../../tasks/api/tasks.client.js';
import type { TaskItem } from '../../tasks/api/tasks.client.js';
import {
  decideApproval,
  type ApprovalItem,
  type DecideApprovalInput,
} from '../../approvals/api/approvals.client.js';
import { useScopeStore } from '../../../shared/scope/useCurrentScope.js';

interface ApprovalMutationContext {
  workspaceId: string;
}

const DEAL_ACTIVITY_TOOLS = new Set([
  'update_deal',
  'update_deal_stage',
  'create_deal_tasks',
  'send_email',
]);

const TASK_WRITE_TOOLS = new Set([
  'create_task',
  'update_task',
  'delete_task',
  'set_task_status',
  'assign_task',
  'link_task',
  'unlink_task',
  'set_task_due_date',
  'create_deal_tasks',
]);
const NOTE_WRITE_TOOLS = new Set([
  'create_note',
  'update_note',
  'delete_note',
  'link_note',
  'unlink_note',
  // B3 touch strategy: an approved create_deal_tasks run also creates the
  // draft notes, so the notes list must refresh alongside the tasks.
  'create_deal_tasks',
]);
const BULK_DEAL_TOOLS = new Set([
  'bulk_create_deals',
  'bulk_update_deals',
  'bulk_delete_deals',
  'bulk_set_deal_stage',
  'bulk_set_deal_owner',
  'bulk_set_deal_tags',
]);
const BULK_TASK_TOOLS = new Set([
  'bulk_create_tasks',
  'bulk_update_tasks',
  'bulk_delete_tasks',
  'bulk_set_task_status',
  'bulk_set_task_assignee',
  'bulk_set_task_due_date',
]);
const BULK_NOTE_TOOLS = new Set([
  'bulk_create_notes',
  'bulk_update_notes',
  'bulk_delete_notes',
  'bulk_link_notes',
  'bulk_unlink_notes',
]);
const CONTACT_WRITE_TOOLS = new Set([
  'create_contact',
  'update_contact',
  'delete_contact',
  'set_contact_tags',
  'set_contact_owner',
  'link_contact_company',
  'unlink_contact_company',
  'create_contact_interaction',
  'update_contact_interaction',
  'delete_contact_interaction',
]);
const COMPANY_WRITE_TOOLS = new Set([
  'create_company',
  'update_company',
  'delete_company',
  'set_company_tags',
  'set_company_owner',
  'link_company_relation',
  'unlink_company_relation',
]);
const BULK_CONTACT_TOOLS = new Set([
  'bulk_create_contacts',
  'bulk_update_contacts',
  'bulk_delete_contacts',
  'bulk_set_contact_tags',
  'bulk_set_contact_owner',
  'bulk_link_contact_company',
  'bulk_unlink_contact_company',
]);
const BULK_COMPANY_TOOLS = new Set([
  'bulk_create_companies',
  'bulk_update_companies',
  'bulk_delete_companies',
  'bulk_set_company_tags',
  'bulk_set_company_owner',
  'bulk_link_companies',
  'bulk_unlink_companies',
]);
const R9_CALENDAR_TOOLS = new Set([
  'create_calendar_event',
  'update_calendar_event',
  'cancel_calendar_event',
  'reschedule_calendar_event',
  'link_calendar_event_participant',
  'unlink_calendar_event_participant',
  'bulk_create_calendar_events',
  'bulk_update_calendar_events',
  'bulk_cancel_calendar_events',
  'bulk_reschedule_calendar_events',
  'bulk_link_calendar_event_participants',
  'bulk_unlink_calendar_event_participants',
]);
const R9_INTERACTION_TOOLS = new Set([
  'bulk_create_contact_interactions',
  'bulk_update_contact_interactions',
  'bulk_delete_contact_interactions',
]);
const R9_DOCUMENT_TOOLS = new Set([
  'create_document',
  'update_document',
  'delete_document',
  'link_document',
  'unlink_document',
  'transition_document_status',
  'bulk_create_documents',
  'bulk_update_documents',
  'bulk_delete_documents',
]);
const R9_TEMPLATE_TOOLS = new Set([
  'create_template',
  'update_template',
  'delete_template',
  'activate_template',
  'deactivate_template',
  'bulk_create_templates',
  'bulk_update_templates',
  'bulk_delete_templates',
  'bulk_activate_templates',
  'bulk_deactivate_templates',
]);
const R9_ATTACHMENT_TOOLS = new Set([
  'link_attachment',
  'unlink_attachment',
  'delete_attachment',
  'bulk_link_attachments',
  'bulk_unlink_attachments',
  'bulk_delete_attachments',
]);

function extractDealIdFromToolPayload(payload: Record<string, unknown>): string | null {
  if (typeof payload.toolName !== 'string' || !DEAL_ACTIVITY_TOOLS.has(payload.toolName)) {
    return null;
  }

  const args = payload.args;
  if (typeof args !== 'object' || args === null || Array.isArray(args)) {
    return null;
  }

  const dealId = (args as Record<string, unknown>).dealId;
  return typeof dealId === 'string' && dealId.length > 0 ? dealId : null;
}

function record(value: unknown): Record<string, unknown> | null {
  return typeof value === 'object' && value !== null && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : null;
}

function id(value: unknown): string | null {
  return typeof value === 'string' && value.length > 0 ? value : null;
}

type RelatedEntity = 'task' | 'deal' | 'company' | 'contact' | 'document' | 'template';

function isRelatedEntity(value: string): value is RelatedEntity {
  return (
    value === 'task' ||
    value === 'deal' ||
    value === 'company' ||
    value === 'contact' ||
    value === 'document' ||
    value === 'template'
  );
}

function relatedEntities(
  payload: Record<string, unknown>,
): ReadonlyMap<RelatedEntity, Set<string>> {
  const args = record(payload.args) ?? {};
  const related = new Map<RelatedEntity, Set<string>>();
  const add = (entity: RelatedEntity, value: unknown) => {
    const entityId = id(value);
    if (!entityId) return;
    const ids = related.get(entity) ?? new Set<string>();
    ids.add(entityId);
    related.set(entity, ids);
  };
  add('deal', args.dealId);
  add('company', args.companyId);
  add('contact', args.contactId);
  add('document', args.documentId);

  const relation = record(args.relation);
  if (relation && typeof relation.namespace === 'string') {
    const namespace = relation.namespace;
    if (isRelatedEntity(namespace)) {
      add(namespace, relation.id);
    }
  }
  if (typeof args.link === 'string') {
    const link = args.link;
    if (isRelatedEntity(link)) {
      add(link, args.targetId);
    }
  }
  if (Array.isArray(args.links)) {
    for (const item of args.links) {
      const link = record(item);
      if (!link || typeof link.namespace !== 'string') continue;
      const namespace = link.namespace;
      if (isRelatedEntity(namespace)) {
        add(namespace, link.id);
      }
    }
  }
  return related;
}

function cachedUnlinkedTaskRelation(
  task: TaskItem | undefined,
  relation: unknown,
): ReadonlyMap<RelatedEntity, Set<string>> {
  const requestedRelation = typeof relation === 'string' ? relation : null;
  const related = new Map<RelatedEntity, Set<string>>();
  if (!task || !requestedRelation || !isRelatedEntity(requestedRelation)) return related;

  const relationId =
    requestedRelation === 'deal'
      ? task.deal?.id
      : requestedRelation === 'company'
        ? task.company?.id
        : requestedRelation === 'contact'
          ? task.contact?.id
          : requestedRelation === 'document'
            ? (task.document?.id ?? task.documentId)
            : undefined;
  if (relationId) related.set(requestedRelation, new Set([relationId]));
  return related;
}

function mergeRelatedEntities(
  target: Map<RelatedEntity, Set<string>>,
  source: ReadonlyMap<RelatedEntity, Set<string>>,
): void {
  for (const [entity, ids] of source) {
    const targetIds = target.get(entity) ?? new Set<string>();
    for (const entityId of ids) targetIds.add(entityId);
    target.set(entity, targetIds);
  }
}

function resultId(payload: Record<string, unknown>, key: 'taskId' | 'noteId'): string | null {
  const args = record(payload.args);
  const result = record(payload.result) ?? record(payload.output);
  return id(result?.[key]) ?? id(args?.[key]);
}

export function useApprovalMutation() {
  const queryClient = useQueryClient();

  return useMutation<ApprovalItem, Error, DecideApprovalInput, ApprovalMutationContext>({
    mutationFn: decideApproval,
    onMutate: () => ({
      workspaceId: useScopeStore.getState().scope?.workspaceId ?? 'anonymous',
    }),
    // Without this the decision failed silently: the card stayed "pending"
    // and the user could not tell a network error from a slow server.
    onError: () => {
      void message.error(i18n.t('crm.ai_chat.error.approval_decide_failed'));
    },
    onSuccess: (approval, _input, context) => {
      const scopeState = useScopeStore.getState();
      if (
        scopeState.isSwitchingWorkspace ||
        scopeState.scope?.workspaceId !== context.workspaceId
      ) {
        return;
      }

      const { workspaceId } = context;
      const invalidations = [
        queryClient.invalidateQueries({
          queryKey: ['approvals', workspaceId],
        }),
      ];

      if (approval.status === 'approved' && approval.executionOutcome?.status === 'succeeded') {
        const toolName = approval.payload.toolName;
        if (typeof toolName === 'string' && BULK_DEAL_TOOLS.has(toolName)) {
          invalidations.push(queryClient.invalidateQueries({ queryKey: ['deals', workspaceId] }));
          invalidations.push(
            queryClient.invalidateQueries({ queryKey: ['activities', workspaceId] }),
          );
        }
        if (typeof toolName === 'string' && BULK_TASK_TOOLS.has(toolName)) {
          invalidations.push(queryClient.invalidateQueries({ queryKey: ['tasks', workspaceId] }));
          invalidations.push(
            queryClient.invalidateQueries({ queryKey: ['activities', workspaceId] }),
          );
        }
        if (typeof toolName === 'string' && BULK_NOTE_TOOLS.has(toolName)) {
          invalidations.push(queryClient.invalidateQueries({ queryKey: ['notes', workspaceId] }));
          invalidations.push(
            queryClient.invalidateQueries({ queryKey: ['activities', workspaceId] }),
          );
          if (toolName === 'bulk_link_notes' || toolName === 'bulk_unlink_notes') {
            invalidations.push(
              queryClient.invalidateQueries({ queryKey: ['meetings', workspaceId] }),
            );
          }
        }
        if (typeof toolName === 'string' && BULK_CONTACT_TOOLS.has(toolName)) {
          invalidations.push(
            queryClient.invalidateQueries({ queryKey: ['contacts', workspaceId] }),
          );
          invalidations.push(
            queryClient.invalidateQueries({ queryKey: ['companies', workspaceId] }),
          );
          invalidations.push(
            queryClient.invalidateQueries({ queryKey: ['activities', workspaceId] }),
          );
        }
        if (typeof toolName === 'string' && BULK_COMPANY_TOOLS.has(toolName)) {
          invalidations.push(
            queryClient.invalidateQueries({ queryKey: ['companies', workspaceId] }),
          );
          invalidations.push(
            queryClient.invalidateQueries({ queryKey: ['contacts', workspaceId] }),
          );
          invalidations.push(queryClient.invalidateQueries({ queryKey: ['deals', workspaceId] }));
          invalidations.push(queryClient.invalidateQueries({ queryKey: ['tasks', workspaceId] }));
          invalidations.push(
            queryClient.invalidateQueries({ queryKey: ['documents', workspaceId] }),
          );
          invalidations.push(
            queryClient.invalidateQueries({ queryKey: ['activities', workspaceId] }),
          );
        }
        if (typeof toolName === 'string' && DEAL_ACTIVITY_TOOLS.has(toolName)) {
          invalidations.push(queryClient.invalidateQueries({ queryKey: ['deals', workspaceId] }));
        }
        if (typeof toolName === 'string' && TASK_WRITE_TOOLS.has(toolName)) {
          invalidations.push(queryClient.invalidateQueries({ queryKey: ['tasks', workspaceId] }));
          const taskId = resultId(approval.payload, 'taskId');
          if (taskId) {
            invalidations.push(
              queryClient.invalidateQueries({
                queryKey: getTaskDetailQueryKey(workspaceId, taskId),
              }),
              queryClient.invalidateQueries({
                queryKey: getEntityActivitiesQueryKey(workspaceId, 'task', taskId),
              }),
            );
          }
        }

        if (typeof toolName === 'string' && NOTE_WRITE_TOOLS.has(toolName)) {
          invalidations.push(queryClient.invalidateQueries({ queryKey: ['notes', workspaceId] }));
        }
        if (typeof toolName === 'string' && CONTACT_WRITE_TOOLS.has(toolName)) {
          invalidations.push(
            queryClient.invalidateQueries({ queryKey: ['contacts', workspaceId] }),
          );
          invalidations.push(
            queryClient.invalidateQueries({ queryKey: ['companies', workspaceId] }),
          );
        }
        if (typeof toolName === 'string' && COMPANY_WRITE_TOOLS.has(toolName)) {
          invalidations.push(
            queryClient.invalidateQueries({ queryKey: ['companies', workspaceId] }),
          );
          invalidations.push(
            queryClient.invalidateQueries({ queryKey: ['contacts', workspaceId] }),
          );
          invalidations.push(queryClient.invalidateQueries({ queryKey: ['deals', workspaceId] }));
          invalidations.push(queryClient.invalidateQueries({ queryKey: ['tasks', workspaceId] }));
          invalidations.push(
            queryClient.invalidateQueries({ queryKey: ['documents', workspaceId] }),
          );
        }
        if (typeof toolName === 'string' && R9_CALENDAR_TOOLS.has(toolName)) {
          invalidations.push(
            queryClient.invalidateQueries({ queryKey: ['meetings', workspaceId] }),
          );
          invalidations.push(
            queryClient.invalidateQueries({ queryKey: ['activities', workspaceId] }),
          );
        }
        if (typeof toolName === 'string' && R9_INTERACTION_TOOLS.has(toolName)) {
          invalidations.push(
            queryClient.invalidateQueries({ queryKey: ['contacts', workspaceId] }),
          );
          invalidations.push(
            queryClient.invalidateQueries({ queryKey: ['activities', workspaceId] }),
          );
        }
        if (typeof toolName === 'string' && R9_DOCUMENT_TOOLS.has(toolName)) {
          invalidations.push(
            queryClient.invalidateQueries({ queryKey: ['documents', workspaceId] }),
          );
          invalidations.push(queryClient.invalidateQueries({ queryKey: ['tasks', workspaceId] }));
          invalidations.push(
            queryClient.invalidateQueries({ queryKey: ['activities', workspaceId] }),
          );
        }
        if (typeof toolName === 'string' && R9_TEMPLATE_TOOLS.has(toolName)) {
          invalidations.push(
            queryClient.invalidateQueries({ queryKey: ['templates', workspaceId] }),
          );
          invalidations.push(
            queryClient.invalidateQueries({ queryKey: ['activities', workspaceId] }),
          );
        }
        if (typeof toolName === 'string' && R9_ATTACHMENT_TOOLS.has(toolName)) {
          invalidations.push(
            queryClient.invalidateQueries({ queryKey: ['attachments', workspaceId] }),
          );
          invalidations.push(
            queryClient.invalidateQueries({ queryKey: ['activities', workspaceId] }),
          );
        }

        const related = new Map(relatedEntities(approval.payload));
        if (toolName === 'unlink_task') {
          const args = record(approval.payload.args);
          const taskId = id(args?.taskId);
          if (taskId) {
            mergeRelatedEntities(
              related,
              cachedUnlinkedTaskRelation(
                queryClient.getQueryData<TaskItem>(getTaskDetailQueryKey(workspaceId, taskId)),
                args?.link,
              ),
            );
          }
        }
        for (const [entity, ids] of related) {
          for (const entityId of ids) {
            const detailKey =
              entity === 'task'
                ? getTaskDetailQueryKey(workspaceId, entityId)
                : entity === 'deal'
                  ? getDealDetailQueryKey(workspaceId, entityId)
                  : entity === 'contact'
                    ? getContactDetailQueryKey(workspaceId, entityId)
                    : entity === 'company'
                      ? getCompanyDetailQueryKey(workspaceId, entityId)
                      : entity === 'document'
                        ? getDocumentDetailQueryKey(workspaceId, entityId)
                        : null;
            if (detailKey) {
              invalidations.push(queryClient.invalidateQueries({ queryKey: detailKey }));
            }
            invalidations.push(
              queryClient.invalidateQueries({
                queryKey: getEntityActivitiesQueryKey(workspaceId, entity, entityId),
              }),
            );
          }
        }

        if (approval.payload.toolName === 'write_memory') {
          invalidations.push(
            queryClient.invalidateQueries({ queryKey: ['memory', 'recent', workspaceId] }),
          );
        }

        const dealId = extractDealIdFromToolPayload(approval.payload);
        if (dealId !== null) {
          invalidations.push(
            queryClient.invalidateQueries({
              queryKey: getEntityActivitiesQueryKey(workspaceId, 'deal', dealId),
            }),
          );
        }
      }

      void Promise.allSettled(invalidations);
    },
  });
}
